import { createRootRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { useState, useMemo } from 'react'
import {
  LayoutDashboard,
  Building2,
  Activity,
  Eye,
  Users,
  LogOut,
  Menu,
  X,
  ClipboardList,
  BarChart3,
  Brain,
  Grid3x3,
  FileText,
  Lightbulb,
  Briefcase,
  UserCheck,
  GitMerge,
  ChevronRight,
  ShieldCheck,
  BookOpen,
  Layers,
  CheckSquare,
  Crown,
  ClipboardCheck,
  Sliders,
  GraduationCap,
  Milestone,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { NotificationBell } from '../components/NotificationBell'
import { can } from '../lib/roles'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'

const PUBLIC_PATHS = ['/', '/login', '/login/', '/kinerja-product', '/wawasan-product', '/rekrut-product']

// ─── Module definitions ───────────────────────────────────────────────────────

const MODULES = {
  kinerja: {
    label: 'Kinerja',
    description: 'Performance & Assessments',
    icon: Activity,
    color: 'text-brand-500',
    bg: 'bg-brand-50',
    links: [
      { to: '/kinerja/my-assessments', label: 'My Assessments', icon: ClipboardList },
      { to: '/kinerja/metrics', label: 'Metrics', icon: BarChart3 },
      { to: '/kinerja/psychometrics', label: 'Psychometrics', icon: Brain },
      { to: '/kinerja/tasks/', label: 'Tasks', icon: CheckSquare },
      { to: '/idp', label: 'My Development Plan', icon: Lightbulb },
    ],
    managerLinks: [
      { to: '/kinerja/templates', label: 'Assessment Templates', icon: FileText },
      { to: '/kinerja/assessments', label: 'All Assessments', icon: ClipboardCheck },
      { to: '/kinerja/competencies', label: 'Competency Dictionary', icon: BookOpen },
      { to: '/kinerja/positions', label: 'Positions', icon: Briefcase },
      { to: '/kinerja/org-levels', label: 'Org Levels', icon: Layers },
      { to: '/kinerja/metric-weights', label: 'Metric Weights', icon: Sliders },
      { to: '/kinerja/training-catalogue', label: 'Training Catalogue', icon: GraduationCap },
      { to: '/kinerja/milestone-templates', label: 'Milestone Templates', icon: Milestone },
    ],
  },
  wawasan: {
    label: 'Wawasan',
    description: 'Talent Intelligence',
    icon: Eye,
    color: 'text-navy-600',
    bg: 'bg-navy-50',
    links: [
      { to: '/wawasan/9box', label: '9-Box Grid', icon: Grid3x3 },
      { to: '/reports', label: 'AI Reports', icon: FileText },
      { to: '/idp', label: 'IDP', icon: Lightbulb },
    ],
  },
  rekrut: {
    label: 'Rekrut',
    description: 'Recruitment Pipeline',
    icon: Users,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    links: [
      { to: '/rekrut/jobs', label: 'Job Postings', icon: Briefcase },
      { to: '/rekrut/candidates', label: 'Candidates', icon: UserCheck },
      { to: '/rekrut/pipeline', label: 'Pipeline', icon: GitMerge },
    ],
  },
} as const

type ModuleKey = keyof typeof MODULES

function getActiveModule(pathname: string): ModuleKey | null {
  if (pathname.startsWith('/kinerja')) return 'kinerja'
  if (pathname.startsWith('/wawasan') || pathname.startsWith('/idp') || pathname.startsWith('/reports')) return 'wawasan'
  if (pathname.startsWith('/rekrut')) return 'rekrut'
  return null
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function ModuleSidebar({ module: moduleKey, role }: { module: ModuleKey; role: string | undefined }) {
  const mod = MODULES[moduleKey]
  const ModIcon = mod.icon
  const canManage = can(role, 'manage:kinerja')

  const links = [
    ...mod.links,
    ...(moduleKey === 'kinerja' && canManage && 'managerLinks' in mod ? mod.managerLinks : []),
  ]

  return (
    <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r bg-card">
      {/* Module header */}
      <div className={cn('flex items-center gap-3 px-4 py-4 border-b', mod.bg)}>
        <div className={cn('p-1.5 rounded-md', mod.bg)}>
          <ModIcon className={cn('w-4 h-4', mod.color)} />
        </div>
        <div>
          <p className={cn('text-sm font-semibold', mod.color)}>{mod.label}</p>
          <p className="text-xs text-muted-foreground">{mod.description}</p>
        </div>
      </div>

      {/* Plain overflow div — avoids Radix ResizeObserver re-attachment on every render */}
      <div className="flex-1 overflow-y-auto">
        <nav className="py-3 px-2 space-y-0.5">
          {links.map((link) => {
            const Icon = link.icon
            return (
              <Link
                key={`${link.to}-${link.label}`}
                to={link.to as any}
                className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                activeProps={{ className: cn('flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium', mod.color, mod.bg) }}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {link.label}
              </Link>
            )
          })}
        </nav>
      </div>
    </aside>
  )
}

// ─── Mobile sidebar drawer ────────────────────────────────────────────────────

function MobileSidebarDrawer({
  module: moduleKey,
  role,
  open,
  onClose,
}: {
  module: ModuleKey
  role: string | undefined
  open: boolean
  onClose: () => void
}) {
  const mod = MODULES[moduleKey]
  const ModIcon = mod.icon
  const canManage = can(role, 'manage:kinerja')
  const links = [
    ...mod.links,
    ...(moduleKey === 'kinerja' && canManage && 'managerLinks' in mod ? mod.managerLinks : []),
  ]

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onClose} />
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-card shadow-xl lg:hidden flex flex-col">
        <div className={cn('flex items-center justify-between px-4 py-4 border-b', mod.bg)}>
          <div className="flex items-center gap-2">
            <ModIcon className={cn('w-4 h-4', mod.color)} />
            <span className={cn('text-sm font-semibold', mod.color)}>{mod.label}</span>
          </div>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="py-3 px-2 space-y-0.5">
          {links.map((link) => {
            const Icon = link.icon
            return (
              <Link
                key={`${link.to}-${link.label}`}
                to={link.to as any}
                onClick={onClose}
                className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                activeProps={{ className: cn('flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium', mod.color, mod.bg) }}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {link.label}
              </Link>
            )
          })}
        </nav>
      </div>
    </>
  )
}

// ─── Root layout ──────────────────────────────────────────────────────────────
// IMPORTANT: Must be a named function defined OUTSIDE createRootRoute so that
// TanStack Router receives a stable reference and does not unmount/remount the
// layout on every render cycle (which would cause a freeze).

function RootLayout() {
  const { isAuthenticated, logout, user } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const role = user?.role
  // useLocation() only re-renders on pathname changes — much narrower than
  // useRouterState() which subscribes to the entire router state object and
  // re-renders on every browser event including text selection.
  const location = useLocation()
  const isPublicPage = useMemo(
    () => PUBLIC_PATHS.includes(location.pathname),
    [location.pathname],
  )
  const activeModule = useMemo(
    () => getActiveModule(location.pathname),
    [location.pathname],
  )

  if (isPublicPage) {
    return (
      <>
        <Outlet />
        {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools />}
      </>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-background flex flex-col">
        {/* ── Top Bar ── */}
        <header className="h-14 border-b bg-card shrink-0 flex items-center px-4 lg:px-6 gap-4 z-30 sticky top-0">
          {/* Mobile: sidebar toggle (only in module pages) */}
          {activeModule && (
            <button
              className="lg:hidden p-1.5 -ml-1 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/assets/logo.svg" alt="Pelajari" className="h-6 w-auto" />
          </Link>

          <Separator orientation="vertical" className="h-5 hidden sm:block" />

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1 flex-1">
            {isAuthenticated && (
              <>
                <Link
                   to="/dashboard/"
                   className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                   activeProps={{ className: 'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-foreground bg-muted' }}
                 >
                   <LayoutDashboard className="w-3.5 h-3.5" />
                   Dashboard
                 </Link>

                {can(role, 'view:org') && (
                  <Link
                    to="/org/"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    activeProps={{ className: 'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-foreground bg-muted' }}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    Org
                  </Link>
                )}

                <Separator orientation="vertical" className="h-4 mx-1" />

                {/* Module links */}
                {can(role, 'view:kinerja') && (
                  <Link
                     to="/kinerja/"
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                      activeModule === 'kinerja'
                        ? 'font-semibold text-brand-600 bg-brand-50'
                        : 'text-muted-foreground hover:text-brand-600 hover:bg-brand-50'
                    )}
                  >
                    <Activity className="w-3.5 h-3.5" />
                    Kinerja
                    {activeModule === 'kinerja' && <ChevronRight className="w-3 h-3 opacity-50" />}
                  </Link>
                )}

                {can(role, 'view:wawasan') && (
                  <Link
                     to="/wawasan/"
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                      activeModule === 'wawasan'
                        ? 'font-semibold text-navy-700 bg-navy-50'
                        : 'text-muted-foreground hover:text-navy-700 hover:bg-navy-50'
                    )}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Wawasan
                    {activeModule === 'wawasan' && <ChevronRight className="w-3 h-3 opacity-50" />}
                  </Link>
                )}

                {can(role, 'view:rekrut') && (
                  <Link
                     to="/rekrut/"
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                      activeModule === 'rekrut'
                        ? 'font-semibold text-amber-700 bg-amber-50'
                        : 'text-muted-foreground hover:text-amber-700 hover:bg-amber-50'
                    )}
                  >
                    <Users className="w-3.5 h-3.5" />
                    Rekrut
                    {activeModule === 'rekrut' && <ChevronRight className="w-3 h-3 opacity-50" />}
                  </Link>
                )}

                {can(role, 'view:admin') && (
                  <Link
                    to={'/admin' as any}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    activeProps={{ className: 'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-foreground bg-muted' }}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Admin
                  </Link>
                )}

                {role === 'super_admin' && (
                  <Link
                    to={'/superadmin' as any}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-50 transition-colors font-medium"
                    activeProps={{ className: 'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold text-purple-800 bg-purple-50' }}
                  >
                    <Crown className="w-3.5 h-3.5" />
                    Superadmin
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* Right side */}
          <div className="ml-auto flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <NotificationBell />
                <Link
                  to={'/profile' as any}
                  className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-md hover:bg-muted transition-colors"
                >
                  {user?.name ?? 'Profile'}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="hidden sm:flex items-center gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Logout
                </Button>
                {/* Mobile hamburger */}
                <button
                  className="sm:hidden p-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileMenuOpen((v) => !v)}
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </>
            ) : (
              <Link to="/login/" className="text-sm text-muted-foreground hover:text-foreground">
                Login
              </Link>
            )}
          </div>
        </header>

        {/* Mobile top-level menu dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-b bg-card shadow-md z-20">
            <div className="py-2 px-3 space-y-0.5">
              {isAuthenticated && (
                <>
                  <Link to="/dashboard/" onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted"
                    activeProps={{ className: 'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-muted text-foreground' }}>
                    <LayoutDashboard className="w-4 h-4" />Dashboard
                  </Link>
                  {can(role, 'view:kinerja') && (
                    <Link to="/kinerja/" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-brand-50 hover:text-brand-600"
                      activeProps={{ className: 'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-brand-50 text-brand-600' }}>
                      <Activity className="w-4 h-4" />Kinerja
                    </Link>
                  )}
                  {can(role, 'view:wawasan') && (
                    <Link to="/wawasan/" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-navy-50 hover:text-navy-700"
                      activeProps={{ className: 'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-navy-50 text-navy-700' }}>
                      <Eye className="w-4 h-4" />Wawasan
                    </Link>
                  )}
                  {can(role, 'view:rekrut') && (
                    <Link to="/rekrut/" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-amber-50 hover:text-amber-700"
                      activeProps={{ className: 'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-amber-50 text-amber-700' }}>
                      <Users className="w-4 h-4" />Rekrut
                    </Link>
                  )}
                  {can(role, 'view:admin') && (
                    <Link to={'/admin' as any} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted"
                      activeProps={{ className: 'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-muted text-foreground' }}>
                      <ShieldCheck className="w-4 h-4" />Admin
                    </Link>
                  )}
                  {role === 'super_admin' && (
                    <Link to={'/superadmin' as any} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-purple-600 hover:bg-purple-50 font-medium"
                      activeProps={{ className: 'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold bg-purple-50 text-purple-800' }}>
                      <Crown className="w-4 h-4" />Superadmin
                    </Link>
                  )}
                  <Separator className="my-1" />
                  <Link to={'/profile' as any} onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted">
                    {user?.name ?? 'Profile'}
                  </Link>
                  <button onClick={() => { logout(); setMobileMenuOpen(false) }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-destructive hover:bg-destructive/10">
                    <LogOut className="w-4 h-4" />Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Body: Sidebar + Main ── */}
        <div className="flex flex-1 min-h-0">
          {/* Left sidebar (desktop) */}
          {activeModule && <ModuleSidebar module={activeModule} role={role} />}

          {/* Mobile sidebar drawer */}
          {activeModule && (
            <MobileSidebarDrawer
              module={activeModule}
              role={role}
              open={mobileSidebarOpen}
              onClose={() => setMobileSidebarOpen(false)}
            />
          )}

          {/* Main content */}
          <main className="flex-1 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools />}
    </>
  )
}

// ─── Route ───────────────────────────────────────────────────────────────────

export const Route = createRootRoute({
  component: RootLayout,
})
